import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { LogsSnackbarComponent } from 'src/app/components/atoms/logs-snackbar/logs-snackbar.component';

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info'
}

export interface SnackbarData {
  message: string;
  button?: string;
  type?: NotificationType;
  icon?: string;
}


/**
 * Service for displaying snackbar notifications.
 *
 * * It uses Angular Material's MatSnackBar to display the notifications.
 * It provides methods for displaying different types of notifications
 * (success, error, warning, info).
 */
@Injectable({
  providedIn: 'root'
})
export class SnackbarService {
  private _snackBar = inject(MatSnackBar);

  /**
   * Default configuration for snackbars
   */
  private defaultConfig: MatSnackBarConfig = {
    duration: 5000,
    horizontalPosition: 'end',
    verticalPosition: 'bottom',
    panelClass: ['custom-snackbar']
  };

  /**
   * Shows a message in a snackbar
   * 
   * @param message The message to display
   * @param duration Duration in seconds (defaults to 5)
   * @param buttonText Text for the action button (defaults to 'Ok')
   * @param type Type of notification (defaults to INFO)
   */
  showMessage(message: string, duration = 5, buttonText = 'Ok', type = NotificationType.INFO): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      duration: duration * 1000,
      panelClass: [...this.defaultConfig.panelClass!, `snackbar-${type}`],
      data: {
        message,
        button: buttonText,
        type,
        icon: this.getIconForType(type)
      }
    };

    this._snackBar.openFromComponent(LogsSnackbarComponent, config);
  }

  /**
   * Shows an error message in a snackbar
   * 
   * @param message The error message to display
   * @param duration Duration in seconds (defaults to 5)
   */
  showError(message: string | undefined, duration = 5): void {
    this.showMessage(message || 'Unkown error', duration, 'Close', NotificationType.ERROR);
  }

  /**
   * Shows a success message in a snackbar
   * 
   * @param message The success message to display
   * @param duration Duration in seconds (defaults to 5)
   */
  showSuccess(message: string, duration = 5): void {
    this.showMessage(message, duration, 'Ok', NotificationType.SUCCESS);
  }

  /**
   * Shows a warning message in a snackbar
   * 
   * @param message The warning message to display
   * @param duration Duration in seconds (defaults to 5)
   */
  showWarning(message: string, duration = 5): void {
    this.showMessage(message, duration, 'Ok', NotificationType.WARNING);
  }

  /**
   * Shows an info message in a snackbar
   * 
   * @param message The info message to display
   * @param duration Duration in seconds (defaults to 5)
   */
  showInfo(message: string, duration = 5): void {
    this.showMessage(message, duration, 'Ok', NotificationType.INFO);
  }

  /**
   * Get the appropriate icon for the notification type
   * 
   * @param type The notification type
   * @returns The icon name
   */
  private getIconForType(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'check_circle';
      case NotificationType.ERROR:
        return 'error';
      case NotificationType.WARNING:
        return 'warning';
      case NotificationType.INFO:
      default:
        return 'info';
    }
  }
}
